import { useState, useEffect } from "react";
import CryptoConverterComponent from "./CryptoConverter.component";
import {
  getConvertedValue,
  getNormalisedFetchConversionRate,
  getUniqueQuickBuyCurrencies,
} from "./CryptoConverter.functions";
import {
  FIAT_CURRENCIES_OPTIONS,
  DEFAULT_CONVERSION_RATE_FETCH_AMOUNT,
  DEFAULT_VALUE_PRECISION_LENGTH,
  DEFAULT_LEFT_CURRENCY,
  DEFAULT_RIGHT_CURRENCY,
  DEFAULT_LEFT_AMOUNT,
  DEFAULT_RIGHT_AMOUNT,
  DEFAULT_POPULAR_PAIR,
} from "../../constants/CryptoConverter";
import { getIconURLFromCurrency } from "../../utils/normalizer/common";
import BrokerTickerAPI from "../../api/brokerTicker";
import ConverterAPI from "../../api/converter";
import { signupCtaHandler } from "../../utils/urls";
import { currencies as currenciesMockData } from "../../mockData/currencies";

const CryptoConverterContainer = ({ query }) => {
  const [state, setState] = useState({
    quickBuyCurrencies: [],
    currencies: currenciesMockData,
    leftAmount: DEFAULT_LEFT_AMOUNT,
    rightAmount: DEFAULT_RIGHT_AMOUNT,
    currenciesOptions: {},
    leftCurrency: DEFAULT_LEFT_CURRENCY,
    rightCurrency: DEFAULT_RIGHT_CURRENCY,
    conversionRate: null,
    isCurrenciesLoading: false,
    isConversionRateFetching: false,
    popularPair: DEFAULT_POPULAR_PAIR,
  });
  const {
    quickBuyCurrencies,
    currencies,
    leftAmount,
    rightAmount,
    currenciesOptions,
    leftCurrency,
    rightCurrency,
    conversionRate,
    isCurrenciesLoading,
    isConversionRateFetching,
    popularPair,
  } = state;

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isCurrenciesLoading: true,
    }));

    Promise.all([
      BrokerTickerAPI.getQuickBuyTickers(),
      ConverterAPI.getConversionRate(
        getNormalisedFetchConversionRate({
          from: leftCurrency,
          to: rightCurrency,
          amount: DEFAULT_CONVERSION_RATE_FETCH_AMOUNT,
        }),
      ),
    ])
      .then((results) => {
        setState((prevState) => ({
          ...prevState,
          isCurrenciesLoading: false,
          quickBuyCurrencies: getUniqueQuickBuyCurrencies(
            results[0]?.data ?? {},
          ),
          conversionRate: results[1]?.data,
        }));
      })
      .catch((err) => {
        setState((prevState) => ({
          ...prevState,
          isCurrenciesLoading: false,
        }));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const formattedCurrenciesOptions = {
      fiat: {
        label: "FIAT",
        options: FIAT_CURRENCIES_OPTIONS.map((option) => ({
          ...option,
          icon: getIconURLFromCurrency(option.value),
        })),
      },
      crypto: {
        label: "CRYPTO",
        options: currencies
          .filter((option) => quickBuyCurrencies.includes(option.type))
          .map((option) => ({
            value: option.type,
            icon: getIconURLFromCurrency(option.type),
            label: option.name,
          })),
      },
    };
    setState((prevState) => ({
      ...prevState,
      currenciesOptions: formattedCurrenciesOptions,
    }));
  }, [currencies, quickBuyCurrencies]);

  // cta click redirection function
  const ctaClick = () => {
    signupCtaHandler(query);
  };

  const handleLeftAmountChange = (name, value) => {
    setState((prevState) => ({
      ...prevState,
      leftAmount: value,
      rightAmount: getConvertedValue(
        conversionRate?.amount,
        value,
        DEFAULT_VALUE_PRECISION_LENGTH,
      ),
    }));
  };
  const handleRightAmountChange = (name, value) => {
    setState((prevState) => ({
      ...prevState,
      rightAmount: value,
      leftAmount: getConvertedValue(
        conversionRate?.amount,
        value,
        DEFAULT_VALUE_PRECISION_LENGTH,
        true,
      ),
    }));
  };
  const handleLeftCurrencySelect = (name, value) => {
    setState((prevState) => ({
      ...prevState,
      leftCurrency: value,
      popularPair: `${value}${rightCurrency}`,
    }));
  };
  const handleRightCurrencySelect = (name, value) => {
    setState((prevState) => ({
      ...prevState,
      rightCurrency: value,
      popularPair: `${leftCurrency}${value}`,
    }));
  };

  const handlePopularPairClick = (pair) => {
    setState((prevState) => ({
      ...prevState,
      leftCurrency: pair.from.toLowerCase(),
      rightCurrency: pair.to.toLowerCase(),
      popularPair: `${pair.from.toLowerCase()}${pair.to.toLowerCase()}`,
    }));
  };

  useEffect(() => {
    const fetchConversionRate = async () => {
      setState((prevState) => ({
        ...prevState,
        isConversionRateFetching: true,
      }));
      const normalizedParams = getNormalisedFetchConversionRate({
        from: leftCurrency,
        to: rightCurrency,
        amount: DEFAULT_CONVERSION_RATE_FETCH_AMOUNT,
      });
      try {
        const { data } = await ConverterAPI.getConversionRate(normalizedParams);
        setState((prevState) => ({
          ...prevState,
          isConversionRateFetching: false,
          ...(data ? { conversionRate: data } : {}),
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          isConversionRateFetching: false,
        }));
      }
    };

    fetchConversionRate();
  }, [leftCurrency, rightCurrency]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      rightAmount: getConvertedValue(
        conversionRate?.amount,
        leftAmount,
        DEFAULT_VALUE_PRECISION_LENGTH,
      ),
    }));
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversionRate]);
  return (
    <CryptoConverterComponent
      {...{
        ctaClick,
        handleLeftAmountChange,
        handleRightAmountChange,
        handleLeftCurrencySelect,
        handleRightCurrencySelect,
        handlePopularPairClick,
        leftAmount,
        rightAmount,
        currenciesOptions,
        leftCurrency,
        rightCurrency,
        isCurrenciesLoading,
        isConversionRateFetching,
        popularPair,
      }}
    />
  );
};

export default CryptoConverterContainer;
